import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import { ErrorMessage } from "@/common/components/ErrorMessage";
import { ImageUploader } from "@/common/components/ImageUploader";
import MaskedTextField from "@/common/components/MaskedTextField";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import SelectController from "@/common/components/SelectController";
import { ShowHeader } from "@/common/components/ShowHeader";
import Tabs from "@/common/components/Tabs";
import TextField from "@/common/components/TextField";
import { nameMask, phoneMask } from "@/common/constants/masks.constant";
import useBlobs from "@/common/hooks/queries/useBlobs";
import useCompany from "@/common/hooks/queries/useCompany";
import useCompanyUser from "@/common/hooks/queries/useCompanyUser";
import { useDriverLicenseCategories } from "@/common/hooks/queries/useDriverLicenseCategories";
import useDebounce from "@/common/hooks/useDebounce";
import { useGoBack } from "@/common/hooks/useGoBack";
import useUserPermissions from "@/common/hooks/useUserPermissions";
import { Mapper } from "@/common/services/mapper";
import { CompanyBranchData } from "@/common/types/companyBranches.type";
import { DriverLicenseCategoryData } from "@/common/types/driverLicenseCategory";
import { RoleData } from "@/common/types/roles";
import { checkIsImage } from "@/common/utils/fileValidator";
import { FormTransformers } from "@/common/utils/formTransformers";
import { removeSpecialCharacters } from "@/common/utils/unFormatters";
import { useAvailableCompanyBranches } from "@/features/privateCompanies/hooks/companyBranches/useAvailableCompanyBranches";
import { useCompanyUserRoles } from "@/features/privateCompanies/hooks/useCompanyUserRoles";
import useUpdateCompanyUser from "@/features/privateCompanies/hooks/useUpdateCompanyUser";
import {
  companyUserSchema,
  CompanyUserValidationSchema,
} from "@/features/privateCompanies/schemas/create.schema";
import { companyUserDefaultValues } from "@/features/privateCompanies/utils/companyUserDefaultValues";
import { statusOptions } from "@/features/users/constants/statusOptions.constant";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronsRightIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";

const Create = () => {
  const { companyId, id } = useParams();
  const goBack = useGoBack();
  const queryClient = useQueryClient();
  const [messages, setMessages] = useState<string[] | boolean>(false);
  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions();

  const [searchBranch, setSearchBranch] = useDebounce("");
  const [searchRoles, setSearchRoles] = useDebounce("");
  const [searchDriverLicenseCategories, setSearchDriverLicenseCategories] = useDebounce("");

  const [file, setFile] = useState<Blob>();
  const [profilePictureBlobId, setProfilePictureBlobId] = useState<string>();

  const { mutateAsync: uploadBlob, isPending: uploadBlobIsPending } = useBlobs();

  const {
    data: company,
    isPending: companyIsPending,
  } = useCompany({ id: companyId || "" });

  const {
    data: companyUser,
    isPending: companyUserIsPending
  } = useCompanyUser(
    { id: id || "" },
  );

  const { data: companyBranchOptions } = useAvailableCompanyBranches({
    companyId,
    filters: { search: searchBranch },
    mapper: Mapper.mapToOptions<CompanyBranchData>({
      labelFieldName: "display_name",
      valueFieldName: "id",
    }),
  });

  const { data: rolesOptions } = useCompanyUserRoles({
    filters: { search: searchRoles },
    mapper: Mapper.mapToOptions<RoleData>({
      labelFieldName: "display_name",
      valueFieldName: "id",
    }),
  });

  const { data: driverLicenseCategoriesOptions } = useDriverLicenseCategories({
    filters: { search: searchDriverLicenseCategories },
    mapper: Mapper.mapToOptions<DriverLicenseCategoryData>({
      labelFieldName: "display_name",
      valueFieldName: "id",
    }),
  });

  const canUpdateCpf =
    !isUserPermissionsPending &&
    hasUserPermission("Admin::Client::Users::Update", ["can_update_cpf"]);

  const {
    register,
    handleSubmit,
    control,
    setError,
    setValue,
    clearErrors,
    reset,
    formState: { errors },
  } = useForm<CompanyUserValidationSchema>({
    resolver: zodResolver(companyUserSchema),
    defaultValues: companyUserDefaultValues(companyUser),
  });

  useEffect(() => {
    if (companyUser) {
      reset(companyUserDefaultValues(companyUser), {
        keepDirty: true,
      });
    }
  }, [companyUser, reset]);

  const { mutate: updateClientUser, isPending: updateClientUserIsPending } =
    useUpdateCompanyUser({
      companyUser,
      contactId: id,
      companyId,
      profilePictureBlobId,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["companyUser", id] });
        goBack({ fallback: `/companies/${companyId}/users/${id}` });
      },
      onError: FormTransformers.errorsTransformer<CompanyUserValidationSchema>({
        setError,
        setMessages,
      }),
    });

  const onSubmit = (form: CompanyUserValidationSchema) =>
    updateClientUser({
      form,
    });

  const cpfTooltipId = "tooltip-user-cannot-edit-cpf";
  const showCpfNoPermissionTooltip = !isUserPermissionsPending && !canUpdateCpf;
  const shouldDisableCpfField =
    updateClientUserIsPending || isUserPermissionsPending || !canUpdateCpf;

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <ShowHeader
          goBack
          goBackProps={{
            fallback: `/companies/${companyId}/users/${id}`,
          }}
          isLoading={companyIsPending || companyUserIsPending}
          titleClassName="font-normal text-md text-white-400"
          title={company?.general_information?.fantasy_name}
          subtitle={(
            <div className="flex gap-2 items-center">
              <h2 className="text-white-400">
                {companyUser?.full_name}
              </h2>

              <ChevronsRightIcon className="text-white-400" />

              <h2>
                Editar usuário
              </h2>
            </div>
          )}
          buttons={(
            <Button
              type="submit"
              className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
              disabled={updateClientUserIsPending || uploadBlobIsPending}
              isLoading={updateClientUserIsPending}
            >
              Confirmar
            </Button>
          )}
        />

        <Tabs
          tabsListClassName="w-full bg-transparent rounded-none"
          tabsTriggerClassName="h-8 w-full rounded-none border-b border-[#444444] text-white-800 text-xs data-[state=active]:bg-transparent data-[state=active]:text-white-800 data-[state=active]:shadow-none data-[state=active]:border-blue-800"
          tabsContentClassName="mt-6"
          tabsList={[
            {
              label: "Dados",
              value: "",
              body: (
                <Box className="flex flex-col gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)] overflow-y-auto h-[calc(100vh-192px)]">
                  <p className="text-lg font-bold">Dados pessoais</p>
                  <div className="flex flex-col gap-1">
                    <ImageUploader
                      isLoading={false}
                      label="Foto"
                      name="profile-picture"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = (
                          e.target as HTMLInputElement & { files: FileList }
                        ).files?.[0];
                        if (!file || !checkIsImage(file)) {
                          return;
                        }
                        setFile(file);
                        const response = await uploadBlob({
                          file: file as File,
                          filename: (file as File).name,
                        });
                        setProfilePictureBlobId(response?.data?.data?.id);
                      }}
                      currentImageUrl={
                        file
                          ? URL.createObjectURL(file)
                          : companyUser?.profile_picture?.url
                            ? companyUser?.profile_picture?.url
                            : undefined
                      }
                      disabled={updateClientUserIsPending || uploadBlobIsPending}
                    />

                    {errors.profile_picture?.message && (
                      <ErrorMessage
                        name="profile_picture"
                        message={errors.profile_picture?.message as string}
                      />
                    )}
                  </div>
                  <div className="flex gap-2 w-full justify-between">
                    <div
                      className="w-3/4"
                      data-tooltip-id={
                        showCpfNoPermissionTooltip ? cpfTooltipId : undefined
                      }
                    >
                      {shouldDisableCpfField && (
                        <p className="text-xs text-white-800 mb-1">
                          CPF
                          <span className="text-danger-soft"> *</span>
                        </p>
                      )}
                      <Controller
                        control={control}
                        name="cpf"
                        render={({ field }) => (
                          <MaskedTextField
                            label={shouldDisableCpfField ? undefined : "CPF"}
                            markAsRequired
                            mask="000.000.000-00"
                            value={field.value || ""}
                            onChange={(event) => {
                              clearErrors("cpf");
                              field.onChange(event.target.value);
                            }}
                            onBlur={field.onBlur}
                            name={field.name}
                            ref={field.ref}
                            errorMessage={errors.cpf?.message || undefined}
                            readOnly={shouldDisableCpfField}
                            className={
                              shouldDisableCpfField
                                ? "opacity-60 pointer-events-none"
                                : ""
                            }
                          />
                        )}
                      />

                      <Button variant="link">Importar dados</Button>
                    </div>


                    <Controller
                      control={control}
                      name="date_of_birth"
                      render={({ field }) => (
                        <MaskedTextField
                          label="Data de nascimento"
                          markAsRequired
                          mask="00/00/0000"
                          className="w-1/4"
                          value={field.value || ""}
                          onChange={(event) => {
                            clearErrors("date_of_birth");
                            field.onChange(event.target.value);
                          }}
                          onBlur={field.onBlur}
                          name={field.name}
                          ref={field.ref}
                          errorMessage={
                            errors.date_of_birth?.message || undefined
                          }
                          disabled={updateClientUserIsPending}
                        />
                      )}
                    />
                  </div>
                  <div className="flex gap-2 w-full justify-between">
                    <Controller
                      control={control}
                      name="full_name"
                      render={({ field }) => (
                        <MaskedTextField
                          className="w-3/4"
                          label="Nome"
                          markAsRequired
                          mask={nameMask}
                          maxLength={64}
                          value={field.value || ""}
                          onChange={(event) => {
                            clearErrors("full_name");
                            field.onChange(event.target.value);
                          }}
                          onBlur={field.onBlur}
                          name={field.name}
                          ref={field.ref}
                          errorMessage={errors.full_name?.message || undefined}
                          disabled={updateClientUserIsPending}
                        />
                      )}
                    />

                    <SelectController
                      control={control}
                      name="roles"
                      label="Perfil"
                      markAsRequired
                      className="w-1/4"
                      options={rolesOptions || []}
                      errorMessage={errors.roles?.message || undefined}
                      isMulti
                      onInputChange={setSearchRoles}
                    />
                  </div>
                  <SelectController
                    control={control}
                    name="status"
                    label="Status"
                    markAsRequired
                    options={statusOptions}
                    errorMessage={errors.status?.message || undefined}
                    isDisabled={updateClientUserIsPending}
                  />
                  <TextField
                    label="Matrícula"
                    maxLength={64}
                    {...register("enrollment_number")}
                    errorMessage={
                      errors.enrollment_number?.message || undefined
                    }
                    disabled={updateClientUserIsPending}
                  />
                  <SelectController
                    control={control}
                    name="branch_id"
                    label="Filial"
                    markAsRequired
                    options={companyBranchOptions || []}
                    errorMessage={errors.branch_id?.message || undefined}
                    isDisabled={updateClientUserIsPending}
                    onInputChange={setSearchBranch}
                  />

                  <Controller
                    control={control}
                    name="phone.number"
                    render={({ field }) => (
                      <MaskedTextField
                        label="Telefone"
                        mask={phoneMask}
                        value={field.value || ""}
                        onChange={(event) => field.onChange(event.target.value)}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                        errorMessage={errors.phone?.number?.message || undefined}
                        disabled={updateClientUserIsPending}
                      />
                    )}
                  />

                  <TextField
                    label="Email"
                    {...register("email")}
                    errorMessage={errors.email?.message || undefined}
                    disabled={updateClientUserIsPending}
                  />
                  <div className="flex gap-2 w-full justify-between">
                    <Controller
                      control={control}
                      name="current_driver_license_registration_number"
                      render={({ field }) => (
                        <MaskedTextField
                          label="Número da CNH"
                          mask="00000000000"
                          className="grow"
                          value={field.value || ""}
                          onChange={(event) => {
                            clearErrors("current_driver_license_registration_number");
                            field.onChange(event.target.value);
                          }}
                          onBlur={field.onBlur}
                          name={field.name}
                          ref={field.ref}
                          errorMessage={
                            errors.current_driver_license_registration_number
                              ?.message || undefined
                          }
                          disabled={updateClientUserIsPending}
                        />
                      )}
                    />

                    <SelectController
                      control={control}
                      name="user_driver_license_categories"
                      label="Categorias da CNH"
                      onInputChange={setSearchDriverLicenseCategories}
                      options={driverLicenseCategoriesOptions || []}
                      isMulti
                      className="grow"
                      errorMessage={
                        errors.user_driver_license_categories?.message ||
                        undefined
                      }
                      isDisabled={updateClientUserIsPending}
                    />

                    <Controller
                      control={control}
                      name="current_driver_license_expiration_date"
                      render={({ field }) => (
                        <MaskedTextField
                          label="Data de vencimento"
                          mask="00/00/0000"
                          className="grow"
                          value={field.value || ""}
                          onChange={(event) => {
                            clearErrors("current_driver_license_expiration_date");
                            field.onChange(event.target.value);
                          }}
                          onBlur={field.onBlur}
                          name={field.name}
                          ref={field.ref}
                          errorMessage={
                            errors.current_driver_license_expiration_date
                              ?.message || undefined
                          }
                          disabled={updateClientUserIsPending}
                        />
                      )}
                    />
                  </div>

                  <div className="flex flex-col gap-1">
                    <TextField
                      label="Nome que será impresso no cartão"
                      maxLength={60}
                      {...register("name_on_card", {
                        onChange: (e) => {
                          clearErrors("name_on_card");
                          setValue(
                            "name_on_card",
                            removeSpecialCharacters(e.target.value, {
                              includeSpaces: true,
                            }),
                            {
                              shouldDirty: true,
                            },
                          );
                        },
                      })}
                      valueClassName="uppercase"
                      errorMessage={errors.name_on_card?.message || undefined}
                      disabled={updateClientUserIsPending}
                    />

                    <p className="text-xs">Limite máximo de 60 caracteres</p>
                  </div>
                </Box>
              ),
            },
            {
              label: "Histórico de consumo",
              value: "consumptionHistory",
              disabled: true,
              body: <></>,
            },
            {
              label: "Histórico de ações",
              value: "actionsHistory",
              disabled: true,
              body: <></>,
            },
          ]}
        />
      </form>

      {messages && (
        <ModalMessageSimple
          messages={messages as string[]}
          setMessages={setMessages}
          isError
        />
      )}
    </>
  );
};

export default Create;
